import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { BrandMark } from '@/features/check/BrandMark';
import { titleBarLogoUrl } from '@/features/check/titleBarLogo';
import { useTranslation } from '@/i18n';
import { readGatewayMessage } from './gatewayMessage';
import { gatewayZoom } from '@/payment/cardOnly';
import type { EmbeddedPaymentAction, Location } from '@/api/types';

interface EmbeddedGatewayFrameProps {
    action: EmbeddedPaymentAction;
    /**
     * Whose restaurant this is.
     *
     * The card form is served from the gateway's domain, so this screen is the one place in the flow where
     * everything the guest can see belongs to somebody else. The brand above it is what says they are still
     * where they think they are, at the moment they are typing a card number.
     */
    location: Location;
    /** Names the frame for a screen reader. An unnamed iframe is announced as "frame", which says nothing. */
    title: string;
    /** Called with the gateway's payload, unread. The caller forwards it to `/payments/{id}/complete`. */
    onResult: (result: Record<string, unknown>) => void;
}

/**
 * Card entry hosted by the gateway, inside an iframe on our page.
 *
 * The security of the whole embedded path is the two checks in the listener below, so they are worth stating
 * plainly.
 *
 * `message` is a window-wide event. Every frame, opener and popup that can get a handle on this window can
 * fire one, and the browser will deliver it. A listener that reads `event.data` without checking where it
 * came from will accept a forged payment result from any of them — and the legacy app's listener is exactly
 * that, `window.addEventListener("message", ...)` with no origin test anywhere in it.
 *
 * So: the origin must equal the one the server named, compared exactly, and the source must be this frame's
 * own window. The first stops another site's message being believed; the second stops a different frame on
 * the same trusted origin — an ad, an analytics embed, a second gateway — from speaking for this one.
 *
 * Rejected messages are dropped in silence. Not logged, not counted: a forged payload is attacker-controlled
 * and a genuine one may carry card data, and neither belongs anywhere near a console or a log sink.
 *
 * The frame is deliberately not sandboxed. It is cross-origin, so it cannot reach this document whatever it
 * does; a sandbox would only constrain the gateway's own page, which needs to work, and gateways break under
 * it in ways that surface as a guest unable to pay.
 *
 * ---
 *
 * **The gateway's frame may not carry its own submit button, and ParPay's does not.**
 *
 * AurusPay renders card fields only. It tells the host when those fields become valid, waits for the host to
 * ask, and answers with a token. So the Pay button below is not decoration around the frame — it is the
 * submit control for it, and `handshake` is what pressing it sends.
 *
 * That is why the button appears only when the action carries a handshake. A gateway that has to be asked is
 * exactly a gateway whose submit we own; one that releases its own token has its own button inside the frame,
 * and a second one out here would be a guest tapping ours and nothing happening.
 */
export function EmbeddedGatewayFrame({ action, location, title, onResult }: EmbeddedGatewayFrameProps) {
    const { t } = useTranslation();

    /*
     * Read once per render rather than held in state: nothing changes it while this screen is open, and the
     * flag is already on disk by the time a guest reaches a payment page.
     */
    const zoom = gatewayZoom();
    const frameRef = useRef<HTMLIFrameElement>(null);

    /*
     * The frame decides when the card is complete enough to charge, and says so. Trusting it beats
     * reimplementing card validation out here over an iframe we cannot see into.
     */
    const [canSubmit, setCanSubmit] = useState(false);

    /*
     * Locks the button from the moment the handshake goes out. This is the one place in the app that blocks —
     * a second press is a second token request against a card the guest has already committed, and the whole
     * reason `POST /payments` is the single blocking call is that a double tap here costs real money.
     */
    const [submitted, setSubmitted] = useState(false);

    /*
     * The callback lives in a ref so the listener is attached once per action rather than re-attached on
     * every render. A caller passing an inline arrow — which every caller will — would otherwise tear the
     * listener down and rebuild it constantly, and a message arriving in that window would be missed.
     */
    const onResultRef = useRef(onResult);

    useEffect(() => {
        onResultRef.current = onResult;
    }, [onResult]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== action.origin) return;
            if (event.source !== frameRef.current?.contentWindow) return;

            const message = readGatewayMessage(event.data);

            if (message.kind === 'validity') {
                setCanSubmit(message.canSubmit);
                return;
            }

            if (message.kind === 'result') {
                onResultRef.current(message.payload);
                return;
            }

            /*
             * Unreadable, and the guest is mid-payment with a locked button. Releasing it lets them press
             * again rather than sit on a screen that will never move; the gateway holds the card details, so
             * a second handshake asks the same question rather than starting anything new.
             */
            setSubmitted(false);
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [action.origin]);

    /*
     * The prompt is addressed to the gateway's origin explicitly. Posting to `'*'`, as the legacy client
     * does, offers the message to whatever happens to be loaded in that frame instead.
     */
    const submit = useCallback(() => {
        if (!action.handshake) return;

        setSubmitted(true);
        frameRef.current?.contentWindow?.postMessage(action.handshake, action.origin);
    }, [action.handshake, action.origin]);

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto flex min-h-dvh w-full max-w-[var(--shell-max-width)] flex-col">
                <h1 className="px-5 pt-9 pb-4 text-[1.75rem] font-semibold leading-tight tracking-tight text-balance">
                    <BrandMark logoUrl={titleBarLogoUrl(location)} name={location.name} />
                </h1>

                {/*
                  * Magnified on the tablet, where PAR's own field sizing is close to unusable at arm's
                  * length. Nothing here styles their page - it is cross-origin and rightly closed to us -
                  * but `zoom` on the frame ELEMENT scales what it renders, because the parent never touches
                  * the child's document.
                  *
                  * The frame keeps its full width. `zoom` leaves the element's own box alone and divides the
                  * INNER viewport instead: at 1.3 the frame still fills its column and the hosted page lays
                  * itself out against 591px rather than 768px, so its fields come out a third larger.
                  * Measured, because the obvious guess is that the box scales too - compensating the width
                  * for that left the frame 177px short of its column.
                  *
                  * A phone gets a gentler one, and may get none at all: dividing a 320px viewport would
                  * leave PAR less room than any responsive form expects. See `gatewayZoom`.
                  */}
                <div className="relative flex-1 overflow-hidden bg-card">
                    <iframe
                        ref={frameRef}
                        src={action.url}
                        title={title}
                        allow="payment"
                        className="absolute inset-0 h-full w-full border-0 bg-card"
                        style={zoom > 1 ? { zoom } : undefined}
                    />
                </div>

                {/*
                 * Docked with the frame it submits. An action's control belongs beside what it acts on — the
                 * loyalty redeem error taught this repo the same lesson from the other direction, sitting at
                 * the end of the content flow while its button was in the footer.
                 */}
                {action.handshake && (
                    <footer className="sticky bottom-0 border-t border-border bg-card px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                        <Button
                            size="lg"
                            className="h-[3.25rem] w-full rounded-xl text-base font-semibold"
                            disabled={!canSubmit || submitted}
                            onClick={submit}
                        >
                            {submitted ? t('payment.paying') : t('payment.payCard')}
                        </Button>
                    </footer>
                )}
            </div>
        </div>
    );
}
